<template>
  <v-form v-model="valid" ref="form">
    <v-container fluid class="px-6 mt-4" @click="selectedItem = null">
      <v-row>
        <v-col cols="6" sm="3" md="2">
          <decimal-number-input label="Low inventory point" v-model="product.lowBondedInventoryCases"
                                prefix="" suffix="cases" :precision="0" integer no-minimum
                                hint="Alert if in-bond inventory falls below this amount"/>
        </v-col>
      </v-row>

      <v-row dense v-if="product.mixedPack">
        <v-col cols="8" lg="6">
          <outlined-block title="Products in pack" :rules="[packValid]">
            <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                          :items="product.pack" no-data-text="No products specified"
                          :headers="[{text:'',value:'add',width:'15%',align:'center',cellClass:'pa-0',divider:true},
                                    {text:'Product',value:'product',width:'60%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                                    {text:'Quantity',value:'quantity',width:'25%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt',align:'right'}]" class="no-sort"
                          :item-class="item => item.key=== selectedProduct ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                          @click.native.stop=""
                          @click:row="item => {selectedProduct = item.key; $nextTick(() => this.$refs.form.validate())}"
                          ref="tableBody">
              <template v-slot:item.add="{item,index}">
                <v-btn class="mt-n2" icon @click.stop="deleteProduct(index)" x-small><v-icon>mdi-delete</v-icon></v-btn>
              </template>
              <template v-slot:item.product="{item}">
                <template v-if="selectedProduct === item.key">
                  <v-autocomplete :rules="[fieldRules.required]" dense label="Product" outlined v-model="item.id" :items="products"
                                  item-value="id" item-text="name" class="text-body-2" no-data-text="No products found" autofocus>
                  </v-autocomplete>
                </template>
                <div v-else class="ml-1" :class="{'error--text':!item.id}">{{getProduct(item.id).name}}</div>
              </template>
              <template v-slot:item.quantity="{item}">
                <decimal-number-input v-if="selectedProduct === item.key" label="Quantity" v-model="item.quantity" prefix=""
                      suffix="bottles" :precision="0"/>
                <div v-else class="mr-1" :class="{'error--text':item.quantity < 0}">
                  {{item.quantity }} bottles
                </div>
              </template>
              <template v-slot:footer="{widths}">
                <v-divider/>
                <div @click="selectedProduct = null" style="width:100%" class="py-2 px-0">
                  <div :style="'width:'+widths[0]+'px'" class="text-center">
                    <v-btn fab color="primary" x-small @click.stop="addProduct"><v-icon small>mdi-plus</v-icon></v-btn>
                  </div>
                </div>
              </template>
            </v-data-table>
          </outlined-block>
        </v-col>
      </v-row>

      <v-row dense>
        <v-col cols="12" lg="8">
          <outlined-block title="Bill of Materials" :rules="[bomValid]">
            <v-data-table width="100%" calculate-widths disable-filtering disable-pagination disable-sort hide-default-footer
                          :items="product.bom" no-data-text="No items specified"
                          :headers="[{text:'',value:'add',width:'15%',align:'center',cellClass:'pa-0',divider:true},
                                    {text:'Item',value:'item',width:'35%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                                    {text:'Quantity',value:'quantity',width:'25%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt',align:'right'},
                                    {text:'Per',value:'per',width:'15%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt'},
                                    {text:'Optional',value:'optional',width:'10%',divider:true,class:'px-2',cellClass:'py-2 px-1 pt',align:'center'}]" class="no-sort"
                          :item-class="item => item.key=== selectedItem ? ($vuetify.theme.dark ? 'grey darken-1' : 'grey lighten-4') : ''"
                          @click.native.stop=""
                          @click:row="item => {selectedItem = item.key; $nextTick(() => this.$refs.form.validate())}"
                          ref="tableBody">
              <template v-slot:item.add="{item,index}">
                <v-icon class="handle mt-n2">mdi-drag</v-icon>
                <v-btn class="mt-n2" icon @click.stop="deleteItem(index)" x-small><v-icon>mdi-delete</v-icon></v-btn>
              </template>
              <template v-slot:item.item="{item}">
                <template v-if="selectedItem === item.key">
                  <multi-level-select v-if="categories.length" outlined label="Item"
                                :menuData="eligibleItems" :second.sync="item.id" actionEvent="addItem" actionText="Add new item"
                                @addItem="inputItem=item;itemCreate=true" autofocus/>
                  <v-autocomplete v-else :rules="[fieldRules.required]" dense label="Item" outlined v-model="item.id" :items="items"
                                  item-value="id" item-text="name" class="text-body-2" no-data-text="No items found" autofocus>
                    <template v-slot:prepend-item>
                      <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="inputItem=item;itemCreate=true">
                        <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new item
                      </div>
                    </template>
                  </v-autocomplete>
                </template>
                <div v-else class="ml-1" :class="{'error--text':!item.id}">{{getItem(item.id).name}}</div>
              </template>
              <template v-slot:item.quantity="{item}">
                <decimal-number-input v-if="selectedItem === item.key" label="Quantity" v-model="item.quantity" prefix=""
                      :suffix="getItem(item.id).trackUnits" :precision="getItem(item.id).precision"/>
                <div v-else class="mr-1" :class="{'error--text':item.quantity < 0}">
                  {{item.quantity | number(getItem(item.id).precision)}} {{getItem(item.id).trackUnits}}
                </div>
              </template>
              <template v-slot:item.per="{item}">
                <v-autocomplete v-if="selectedItem === item.key" dense outlined label="Per" v-model="item.per" :items="['Bottle','Case','Bottling']" class="text-body-2"/>
                <div v-else class="ml-1">{{item.per}}</div>
              </template>
              <template v-slot:item.optional="{item}">
                <v-simple-checkbox v-if="selectedItem === item.key" v-model="item.optional" class="mt-n5"/>
                <v-icon v-else-if="item.optional">mdi-check</v-icon>
              </template>
              <template v-slot:footer="{widths}">
                <v-divider/>
                <div @click="selectedItem = null" style="width:100%" class="py-2 px-0">
                  <div :style="'width:'+widths[0]+'px'" class="text-center">
                    <v-btn fab color="primary" x-small @click.stop="addItem"><v-icon small>mdi-plus</v-icon></v-btn>
                  </div>
                </div>
              </template>
            </v-data-table>
          </outlined-block>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="6">
          <v-textarea label="Production notes" v-model="product.description" outlined dense no-resize rows="4" :rules="[fieldRules.maxLength(1000)]"/>
        </v-col>
      </v-row>
      <v-row v-if="$store.getters.company.allowPlanning">
        <v-col cols="12" md="6">
          <production-planning :planning="product.productionPlanningInfo" durationPerUnit="case"/>
        </v-col>
      </v-row>

    </v-container>
    <inventory-items-quick-create v-model="itemCreate" @create="itemCreated"/>
  </v-form>
</template>

<script>
import sortableJS from 'sortablejs';
import InventoryItemsQuickCreate from '@/views/inventory/items/InventoryItemsQuickCreate'
import ProductionPlanning from '@/views/production/ProductionPlanning'
import createHierarchyList from '@/mixins/createHierarchyList'

export default {
  name: 'InventoryProductsEditInfo',
  mixins: [createHierarchyList],
  components: {InventoryItemsQuickCreate,ProductionPlanning},
  props: {
    product: {type:Object, required:true},
  },
  data() {
    return {
      valid: true,
      selectedItem: null,
      items: [],
      selectedProduct: null,
      products: [],
      categories: [],
      itemCreate: false,
      inputItem: null,
      sortable: null
    }
  },
  watch: {
    product: {
      handler() {this.$emit('input', true); this.valid = this.bomValid && this.packValid;},
      immediate: true,
      deep: true
    }
  },
  computed: {
    eligibleItems() {
      const categories = this.createHierarchyList(this.categories);
      for (const category of categories) {
        const sub = this.items.filter(item => item.category?.id === category.id);
        if (sub.length)
          category.menu = sub;
        else
          category.disabled = true;
      }
//      const items = [...categories.filter(category => !category.disabled)];
      const items = categories;
      const leftOver = this.items.filter(item => !item.category?.id);
      if (leftOver.length)
        items.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
      return items;
    },
    bomValid() {
      return this.product.bom.every(item => !!item.id && item.quantity >= 0);
    },
    packValid() {
      return !this.product.mixedPack || !!this.product.pack?.length && this.product.pack?.every(product => !!product.id && product.quantity > 0);
    }
  },
  async created() {
    this.$lazyListLoad(this.categories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]);
    this.$lazyListLoad(this.items, 'items2', 'id,name,trackUnits,precision,category{id}', 100, [{field:'active', comparator:'eq', value:'true'}]);
    this.$lazyListLoad(this.products, 'products2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]);
    this.valid = this.bomValid && this.packValid;
  },
  mounted() {
    const _self = this;
    this.sortable = sortableJS.create(this.$refs.tableBody.$el.querySelector('tbody'), {
      handle: '.handle',
      onEnd({newIndex, oldIndex}) {
        const rowSelected = _self.product.bom.splice(oldIndex, 1)[0];
        _self.product.bom.splice(newIndex, 0, rowSelected);
      }
    })
  },
  beforeDestroy() {
    this.sortable.destroy();
  },
  methods: {
    async itemCreated(item) {
      this.items = (await this.$store.dispatch('items')).filter(item => item.active);
      if (this.inputItem)
        this.inputItem.id = item.id;
    },
    getItem(id) {
      return this.items.find(i => i.id === id) ?? {trackUnits:'', precision:0, name:'MISSING'};
    },
    addItem() {
      const item = {type:'Item',id:null,quantity:0,optional:false,per:'Bottle',key:Date.now()};
      this.product.bom.push(item);
      this.selectedItem = item.key;
      this.$nextTick(() => this.$refs.form.validate());
    },
    deleteItem(index) {
      this.product.bom.splice(index, 1);
    },
    getProduct(id) {
      return this.products.find(p => p.id === id) ?? {name:'MISSING'};
    },
    addProduct() {
      const product = {id:null,quantity:1,key:Date.now()};
      this.product.pack.push(product);
      this.selectedProduct = product.key;
      this.$nextTick(() => this.$refs.form.validate());
    },
    deleteProduct(index) {
      this.product.pack.splice(index, 1);
    }

  }
}
</script>